import { Button } from "@/components/ui/button";
import {
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useAppForm } from "@/hooks/useForm";
import { z } from "zod";
import { Label } from "@/components/ui/label";
import { useApi } from "@/hooks/useApi";
import type { Bloque, Factor } from "../../../@types/Api_Extracted";
import type { TreeNode } from "../../ui/chainSelector";

export function DialogFactor(props: {
	onSuccess: () => void;
	bloques: Bloque[];
	factor: Factor | null;
}) {
	const api = useApi();
	const mutation = api.useMutation(
		"put",
		"/v1/Empresa/Config/CustomFactor/{idFactor}",
	);

	const form = useAppForm({
		defaultValues: props.factor as FormSchema,
		validators: {
			onChange: FormSchema,
		},
		onSubmit: async (data) => {
			await mutation.mutateAsync({
				params: {
					path: { idFactor: props.factor?.idFactor as string },
				},
				body: {
					...data.value,
					idBloque: parseInt(data.value.idBloque as any, 10),
				},
			});
			props.onSuccess();
		},
	});
	if (props.factor === null) return null;

	const bloquesMap = new Map<number, TreeNode>();
	for (const bloque of props.bloques) {
		bloquesMap.set(bloque.idBloque, {
			id: String(bloque.idBloque),
			label: bloque.nombreCorto,
			children: [],
		});
	}
	for (const bloque of props.bloques) {
		if (bloque.idBloque === 0) continue; // Skip root

		const daddy = bloquesMap.get(bloque.idPadre);
		if (!daddy) throw new Error("Bloque padre no encontrado");
		daddy.children?.push(bloquesMap.get(bloque.idBloque) as TreeNode);
	}
	for (const _bloque of props.bloques) {
		const bloque = bloquesMap.get(_bloque.idBloque)!;

		if (bloque.children?.length === 0) bloque.children = undefined;
		else {
			bloque.children = bloque.children?.sort((a, b) =>
				a.label.localeCompare(b.label),
			);
			bloque.children?.unshift({
				id: bloque.id,
				label: bloque.label,
			});
			bloque.id += "(folder)";
		}
		if (bloque.id != "0(folder)") {
			bloquesMap.delete(_bloque.idBloque);
		}
	}

	const values = bloquesMap.get(0)?.children;
	if (values == null) throw new Error("No se encontraron bloques");

	return (
		<DialogContent
			className="sm:max-w-[425px]"
			description="Modal para editar una empresa"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit(e);
				}}
				className="space-y-8"
			>
				<DialogHeader>
					<DialogTitle>Editar Factor</DialogTitle>
				</DialogHeader>
				{/* <SelectControl control={form.control} name="estado" label="Estado" /> */}

				<form.AppField
					name="nombre"
					children={(field) => (
						<div>
							<Label>Nombre del Factor</Label>
							<field.TextField
								placeholder="Nombre del Factor"
								type="text"
								autoComplete="off"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="idBloque"
					children={(field) => (
						<div>
							<Label>Bloque</Label>
							<field.ChainSelectorField label="Factor" values={values} />
						</div>
					)}
				/>
				<form.AppField
					name="unidad"
					children={(field) => (
						<div>
							<Label>Unidad</Label>
							<field.TextField
								placeholder="Unidad"
								type="text"
								autoComplete="unit"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="conversion_equivalente_kilos"
					children={(field) => (
						<div>
							<Label>Conversión Equivalente Kilos</Label>
							<field.NumberField label="kg" />
						</div>
					)}
				/>
				<Label>Emisiones desglosadas</Label>
				<div className="grid grid-cols-3 gap-2">
					<div>
						<Label className="justify-center">CO2</Label>
						<form.AppField
							name="conversion_co2_kg"
							children={(field) => <field.NumberField label="kg" />}
						/>
					</div>

					<div>
						<Label className="justify-center">CH4</Label>
						<form.AppField
							name="conversion_ch4_gramos"
							children={(field) => <field.NumberField label="g" />}
						/>
					</div>

					<div>
						<Label className="justify-center">N2O</Label>
						<form.AppField
							name="conversion_n2o_gramos"
							children={(field) => <field.NumberField label="g" />}
						/>
					</div>
				</div>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" type="button">
							Cancelar
						</Button>
					</DialogClose>

					<Button type="submit">Guardar</Button>
				</DialogFooter>
			</form>
		</DialogContent>
	);
}

const FormSchema = z.object({
	idFactor: z
		.string({
			required_error: "El ID del factor es obligatorio.",
		})
		.min(2, {
			message: "El ID del factor debe tener al menos 2 caracteres.",
		}),
	tipoFactor: z.literal("propio"),
	idBloque: z.number({
		coerce: true,
		required_error: "El ID del bloque es obligatorio.",
	}),
	nombre: z
		.string({
			required_error: "El nombre del factor es obligatorio.",
		})
		.min(2, {
			message: "El nombre del factor debe tener al menos 2 caracteres.",
		}),
	unidad: z
		.string({
			required_error: "La unidad del factor es obligatoria.",
		})
		.min(1, {
			message: "La unidad del factor debe tener al menos 1 carácter.",
		}),

	conversion_equivalente_kilos: z.number({
		required_error: "La conversión equivalente en kilos es obligatoria.",
	}),
	conversion_co2_kg: z.number({
		required_error: "La conversión de CO2 en kg es obligatoria.",
	}),
	conversion_ch4_gramos: z.number({
		required_error: "La conversión de CH4 en gramos es obligatoria.",
	}),
	conversion_n2o_gramos: z.number({
		required_error: "La conversión de N2O en gramos es obligatoria.",
	}),
});
type FormSchema = z.infer<typeof FormSchema>;
